import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { useCartStore } from './cart'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !user.value.is_anonymous)
  const isAdmin = computed(() => ['admin', 'owner', 'developer'].includes(user.value?.role || ''))
  const isStaff = computed(() => isAuthenticated.value && !!user.value?.is_staff)
  const canManageStaff = computed(() => !!user.value?.can_manage_staff)
  const canViewStaffList = computed(() => !!user.value?.can_view_staff_list)
  const userName = computed(() => user.value?.name || user.value?.email || '')
  const isAnonymous = computed(() => !isAuthenticated.value)

  // Actions
  async function initAuth(): Promise<boolean> {
    // Prevent multiple initializations
    if (initialized.value) return isAuthenticated.value

    // Check for stored token
    const storedToken = localStorage.getItem('jwt_token')
    if (storedToken) {
      token.value = storedToken
      const success = await fetchCurrentUser()
      initialized.value = true
      return success
    }

    initialized.value = true
    return false
  }

  async function loginWithGoogle(googleToken: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies
        body: JSON.stringify({ token: googleToken })
      })

      const data = await response.json()

      if (!response.ok) {
        error.value = data.error || 'Authentication failed'
        return false
      }

      user.value = data.user
      token.value = data.token
      localStorage.setItem('jwt_token', data.token)
      initialized.value = true

      // Merge anonymous cart if exists (cookie will be sent automatically)
      const cartStore = useCartStore()
      await cartStore.mergeAnonymousCart()

      return true
    } catch (err) {
      error.value = 'Network error. Please try again.'
      return false
    } finally {
      loading.value = false
    }
  }

  async function fetchCurrentUser(): Promise<boolean> {
    if (!token.value) return false

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        },
        credentials: 'include' // Include cookies
      })

      if (!response.ok) {
        if (response.status === 401) {
          logout()
        }
        return false
      }

      const data = await response.json()
      user.value = data
      return true
    } catch (err) {
      console.error('[Auth] fetchCurrentUser error:', err)
      return false
    }
  }

  async function logout(): Promise<void> {
    try {
      if (token.value) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.value}`
          },
          credentials: 'include'
        })
      }
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem('jwt_token')
      initialized.value = true
    }
  }

  function clearError(): void {
    error.value = null
  }

  return {
    user,
    token,
    loading,
    error,
    initialized,
    isAuthenticated,
    isAdmin,
    isStaff,
    canManageStaff,
    canViewStaffList,
    userName,
    isAnonymous,
    initAuth,
    loginWithGoogle,
    fetchCurrentUser,
    logout,
    clearError
  }
})
